import React, { useState, useMemo } from "react"
import axios from "axios"
import { Modal } from "react-bootstrap"
import { CONST_API_DASHBOARD_KPI_CONSULTANT_ENTRE_SORTI_ENDPOINT } from "../../../../endpoints"
import clsx from "clsx"
import MyModal from "@common-utils/MyModal"
import ConsultantDetails from "../../../../modules/business/consultants/details/ConsultantDetails"

interface Props {
     entreesIds: (string | number)[]
     sortiesIds: (string | number)[]
     dateDebut?: string
     dateFin?: string
}

const ChartEvolutionEffectifsMouvements = ({ entreesIds, sortiesIds }: Props) => {
     const [show, setShow] = useState(false)
     const [loading, setLoading] = useState(false)
     const [consultants, setConsultants] = useState<any[]>([])
     const [searchTerm, setSearchTerm] = useState("")

     // Type mis à jour pour ne garder que "active" (Entrées) et "sorties"
     const [statusFilter, setStatusFilter] = useState<"active" | "sorties">("active")

     // État pour gérer le modal de détails (ID et Nom pour l'en-tête)
     const [viewingConsultant, setViewingConsultant] = useState<{ id: number; name: string } | null>(null)

     // Normalisation des IDs en String pour les comparaisons
     const entreesStr = useMemo(() => entreesIds.map(id => id.toString()), [entreesIds])
     const sortiesStr = useMemo(() => sortiesIds.map(id => id.toString()), [sortiesIds])

     // Fusion des IDs pour l'appel API
     const allIds = useMemo(() => Array.from(new Set([...entreesStr, ...sortiesStr])), [entreesStr, sortiesStr])

     /**
      * Ouvre la modale avec un filtre spécifique
      */
     const handleOpen = async (initialFilter: "active" | "sorties" = "active") => {
          if (allIds.length === 0) return
          setShow(true)
          setLoading(true)
          setSearchTerm("")

          // Applique le filtre demandé (Entrées ou Sorties)
          setStatusFilter(initialFilter)

          try {
               const response = await axios.post(CONST_API_DASHBOARD_KPI_CONSULTANT_ENTRE_SORTI_ENDPOINT, { ids: allIds })
               setConsultants(response.data)
          } catch (error) {
               console.error("Erreur chargement mouvements", error)
               setConsultants([])
          } finally {
               setLoading(false)
          }
     }

     const formatDate = (dateStr: string | null) => {
          if (!dateStr) return "-"
          return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
     }

     const searchedConsultants = useMemo(() => {
          return consultants.filter(c => {
               const search = searchTerm.toLowerCase()
               const fullName = `${c.prenom} ${c.nom}`.toLowerCase()
               const clientName = c.clientPropal?.client?.nom?.toLowerCase() || ""
               return fullName.includes(search) || clientName.includes(search)
          })
     }, [consultants, searchTerm])

     const dynamicCounts = useMemo(() => {
          return {
               active: searchedConsultants.filter(c => entreesStr.includes(c.id.toString())).length,
               sorties: searchedConsultants.filter(c => sortiesStr.includes(c.id.toString())).length,
          }
     }, [searchedConsultants, sortiesStr, entreesStr])

     const finalFilteredList = useMemo(() => {
          if (statusFilter === "active") return searchedConsultants.filter(c => entreesStr.includes(c.id.toString()))
          if (statusFilter === "sorties") return searchedConsultants.filter(c => sortiesStr.includes(c.id.toString()))
          return searchedConsultants
     }, [searchedConsultants, statusFilter, sortiesStr, entreesStr])

     return (
          <>
               {/* Bouton principal */}
               <button
                    type="button"
                    className="btn btn-sm btn-flex border border-gray-300 px-4 py-2 w-100 rounded-3 shadow-none cursor-pointer"
                    onClick={() => handleOpen("active")}
                    disabled={allIds.length === 0}
                    style={{ background: "#F5F8FA", color: "inherit" }}
               >
                    <div className="d-flex align-items-center flex-grow-1">
                         <span className="bullet bullet-vertical h-25px bg-primary me-3"></span>
                         <span className="fs-7 fw-bolder text-gray-700 uppercase">Mouvements de personnel</span>
                    </div>

                    <div className="d-flex align-items-center gap-4">
                         <div className="d-flex align-items-center gap-3 d-none d-md-flex border-start ps-4 border-gray-300">
                              {/* Flèche verte -> Direct vers Entrées */}
                              <div
                                   className="d-flex align-items-center gap-1 cursor-pointer opacity-75-hover"
                                   onClick={e => {
                                        e.stopPropagation()
                                        handleOpen("active")
                                   }}
                              >
                                   <i className="fas fa-arrow-up text-success fs-9"></i>
                                   <span className="text-success fw-boldest fs-7">{entreesIds.length}</span>
                              </div>
                              {/* Flèche rouge -> Direct vers Sorties */}
                              <div
                                   className="d-flex align-items-center gap-1 cursor-pointer opacity-75-hover"
                                   onClick={e => {
                                        e.stopPropagation()
                                        handleOpen("sorties")
                                   }}
                              >
                                   <i className="fas fa-arrow-down text-danger fs-9"></i>
                                   <span className="text-danger fw-boldest fs-7">{sortiesIds.length}</span>
                              </div>
                         </div>
                    </div>
               </button>

               {/* Modal de la liste */}
               <Modal show={show} onHide={() => setShow(false)} size="xl" centered scrollable>
                    <Modal.Header closeButton className="border-0 pb-0">
                         <div className="d-flex flex-column">
                              <Modal.Title className="fw-bolder fs-2 text-dark">Détail des mouvements</Modal.Title>
                         </div>
                    </Modal.Header>

                    <Modal.Body className="pt-0 pb-10">
                         {/* Header de recherche Sticky */}
                         <div className="sticky-top bg-white pt-5 pb-5 mb-8" style={{ zIndex: 10, marginTop: "-1px" }}>
                              <div className="d-flex flex-column gap-4">
                                   <div className="position-relative w-100">
                                        <i className="fas fa-search position-absolute top-50 translate-middle-y ms-4 text-gray-500"></i>
                                        <input
                                             type="text"
                                             className="form-control form-control-solid ps-12 fs-6 fw-bold"
                                             placeholder="Rechercher par consultant ou client..."
                                             value={searchTerm}
                                             onChange={e => setSearchTerm(e.target.value)}
                                        />
                                   </div>

                                   <div className="nav-group nav-group-outline bg-light rounded p-1 d-flex">
                                        <button
                                             className={clsx(
                                                  "btn btn-sm px-4 fw-bold flex-grow-1",
                                                  statusFilter === "active" ? "bg-white shadow-sm text-success" : "text-gray-500"
                                             )}
                                             onClick={() => setStatusFilter("active")}
                                        >
                                             Entrées ({dynamicCounts.active})
                                        </button>
                                        <button
                                             className={clsx(
                                                  "btn btn-sm px-4 fw-bold flex-grow-1",
                                                  statusFilter === "sorties" ? "bg-white shadow-sm text-danger" : "text-gray-500"
                                             )}
                                             onClick={() => setStatusFilter("sorties")}
                                        >
                                             Sorties ({dynamicCounts.sorties})
                                        </button>
                                   </div>
                              </div>
                         </div>

                         {loading ? (
                              <div className="text-center py-20">
                                   <span className="spinner-border text-primary"></span>
                              </div>
                         ) : (
                              <div className="row g-5">
                                   {finalFilteredList.map(c => {
                                        const cIdStr = c.id.toString()
                                        const isSortie = sortiesStr.includes(cIdStr)
                                        const isEntree = entreesStr.includes(cIdStr)
                                        const isDoubleMouvement = isEntree && isSortie

                                        return (
                                             <div
                                                  key={c.id}
                                                  className="col-12 col-md-12 col-lg-6 col-xl-4 cursor-pointer"
                                                  onClick={() => setViewingConsultant({ id: c.id, name: `${c.prenom} ${c.nom}` })} //
                                             >
                                                  <div
                                                       className={clsx(
                                                            "card border border-gray-200 shadow-none p-5 h-100 bg-hover-light transition-all",
                                                            isDoubleMouvement ? "border-primary bg-light-primary" : isSortie ? "border-hover-danger" : "border-hover-success"
                                                       )}
                                                  >
                                                       <div className="d-flex align-items-start gap-4">
                                                            <div className="symbol symbol-50px symbol-circle">
                                                                 <div
                                                                      className={clsx(
                                                                           "symbol-label fs-4 fw-bold",
                                                                           isDoubleMouvement ? "bg-primary text-white" : "bg-light-primary text-primary"
                                                                      )}
                                                                 >
                                                                      {c.prenom.charAt(0)}
                                                                      {c.nom.charAt(0)}
                                                                 </div>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                 <div className="d-flex align-items-center justify-content-between mb-1">
                                                                      <span className="text-dark fw-bolder fs-6">
                                                                           {c.prenom} {c.nom}
                                                                      </span>
                                                                      <div className="d-flex gap-1">
                                                                           {isEntree && <i className="fas fa-arrow-up text-success fs-9" title="Entrée"></i>}
                                                                           {isSortie && <i className="fas fa-arrow-down text-danger fs-9" title="Sortie"></i>}
                                                                      </div>
                                                                 </div>

                                                                 {isDoubleMouvement ? (
                                                                      <span className="badge badge-primary fs-10 mb-2">Entré & Sorti dans la période</span>
                                                                 ) : (
                                                                      <span className="text-muted fw-bold fs-8 d-block mb-1">{c.clientPropal?.client?.nom || "Non affecté"}</span>
                                                                 )}

                                                                 <div className="d-flex gap-2 mt-2">
                                                                      {isEntree && (
                                                                           <span className="badge badge-light-success fs-9 px-2">Entrée: {formatDate(c.dateEmbauche)}</span>
                                                                      )}
                                                                      {isSortie && <span className="badge badge-light-danger fs-9 px-2">Sortie: {formatDate(c.dateSortie)}</span>}
                                                                 </div>
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>
                                        )
                                   })}
                                   {finalFilteredList.length === 0 && (
                                        <div className="col-12 text-center py-10 text-muted fw-bold">Aucun collaborateur ne correspond à ces critères sur cette période.</div>
                                   )}
                              </div>
                         )}
                    </Modal.Body>
               </Modal>

               {/* Modal Détails avec Nom dans l'en-tête */}
               <MyModal title={viewingConsultant?.name} show={viewingConsultant !== null} onCloseCallback={() => setViewingConsultant(null)} fullscreen>
                    {viewingConsultant !== null && <ConsultantDetails idConsultantProp={viewingConsultant.id} />}
               </MyModal>
          </>
     )
}

export default ChartEvolutionEffectifsMouvements
